// ===========================================
// Booking Completion Chart Component
// ===========================================
// Week-by-week completion rates for hybrid bookings
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { BookingCompletionByWeek } from '../../api/reports.api';

interface BookingCompletionChartProps {
  data?: BookingCompletionByWeek[];
  isLoading: boolean;
}

const getBarColor = (completionRate: number): string => {
  if (completionRate >= 90) return '#5cb399'; // Green
  if (completionRate >= 60) return '#FFCE00'; // Yellow
  return '#FFAE9E'; // Coral/Red
};

const BookingCompletionChart: React.FC<BookingCompletionChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Booking Completion Rates by Week
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Booking Completion Rates by Week
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No booking completion data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  const chartData = data.map((week) => ({
    week: week.weekLabel,
    completionRate: week.completionRate,
    enrolled: week.enrolledStudents,
    booked: week.bookedStudents,
    unbooked: week.unbookedCount,
  }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Booking Completion Rates by Week
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Percentage of enrolled students who booked their individual sessions each week. Target: 90%+
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="week" />
          <YAxis
            label={{ value: 'Completion Rate (%)', angle: -90, position: 'insideLeft' }}
            domain={[0, 100]}
          />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.week}</Typography>
                    <Typography variant="body2">
                      Completion Rate: {data.completionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" display="block">
                      Booked: {data.booked} / {data.enrolled}
                    </Typography>
                    <Typography variant="caption" display="block" color="error">
                      Unbooked: {data.unbooked}
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <ReferenceLine y={90} stroke="#5cb399" strokeDasharray="3 3" label="Target (90%)" />
          <Bar dataKey="completionRate" name="Completion Rate (%)">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.completionRate)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Color Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#5cb399', borderRadius: 0.5 }} />
          <Typography variant="caption">Excellent (90%+)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#FFCE00', borderRadius: 0.5 }} />
          <Typography variant="caption">Good (60-90%)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#FFAE9E', borderRadius: 0.5 }} />
          <Typography variant="caption">Needs Improvement (&lt;60%)</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default BookingCompletionChart;
